import { Schematic } from "../../Schematic";
import { joinPackages, joinPaths } from "../../util/join";
import { KtData } from "../kotlin/KtData";
import { KtFile, ktFile } from "../kotlin/KtFile";
import { jsExport, serializable } from "../kotlin/util/annotations";
import { ScaffoldingData } from "../ScaffoldingData";

export function scaffoldModels(schematic: Schematic, data: ScaffoldingData) {
  const fileName = joinPaths(data.currentDir, `${schematic.name}.kt`);
  let file = data.files.get(fileName) as KtFile | undefined;
  if (!file) {
    data.files.set(fileName, (file = ktFile(data)));
  }
  scaffoldModelDeclaration(schematic, { ...data, currentFile: file });
}

function scaffoldModelDeclaration(schematic: Schematic, data: KtData) {
  const schematicKind = data.schematicKinds.get(schematic.kind);
  if (!schematicKind) {
    throw new Error(`Unknown schematic kind ${schematic.kind}`);
  }

  const nDecls = data.currentFile.declarations.length;
  data.currentFile.declarations.push("");
  const decl =
    schematicKind.scaffoldModel?.(schematic, data) ??
    `typealias ${schematic.name} = ${scaffoldType(schematic, data)}`;
  data.currentFile.declarations[nDecls] = schematicKind.scaffoldModel
    ? jsExport(serializable(decl, data), data)
    : decl;
}

export function scaffoldType(schematic: Schematic, data: KtData) {
  const schematicKind = data.schematicKinds.get(schematic.kind);
  if (!schematicKind) {
    throw new Error(`Unknown schematic kind ${schematic.kind}`);
  }

  const schematicPackage =
    schematicKind.package ??
    joinPackages(data.currentPackage, schematic.packageSuffix);
  const schematicName = schematic.name || schematicKind.name;

  if (schematicPackage !== data.currentPackage) {
    data.currentFile.imports.add(joinPackages(schematicPackage, schematicName));
  }

  if (
    schematicKind.package == null &&
    schematicPackage !== data.currentPackage
  ) {
    scaffoldModels(schematic, {
      ...data,
      currentPackage: joinPackages(
        data.currentPackage,
        schematic.packageSuffix,
      ),
      currentDir: joinPaths(
        data.currentDir,
        schematic.packageSuffix?.replace(".", "/"),
      ),
    });
  } else if (schematicKind.scaffoldModel) {
    scaffoldModelDeclaration(schematic, data);
  }

  return (
    (schematicKind.scaffoldType?.(schematic, data) ?? schematicName) +
    (schematic.nullable ? "?" : "")
  );
}

export function scaffoldDefaultValue(
  schematic: Schematic,
  data: KtData,
): string {
  const schematicKind = data.schematicKinds.get(schematic.kind);
  if (!schematicKind) {
    throw new Error(`Unknown schematic kind ${schematic.kind}`);
  }

  return schematic.nullable
    ? "null"
    : typeof schematicKind.defaultValue === "function"
      ? schematicKind.defaultValue(schematic, data)
      : schematicKind.defaultValue;
}
